// src/contexts/AudioContext.js
import React, { createContext, useState, useContext, useEffect, useRef, useMemo } from 'react';
import { storageManager } from '../utils/storageManager';
import { createChapterAudioController } from '../utils/chapterAudioController';

const AudioContext = createContext();

export const useAudioContext = () => {
  return useContext(AudioContext);
};

export const AudioProvider = ({ children }) => {
  const [isAudioPlayerDisabled, setIsAudioPlayerDisabled] = useState(() => {
    return storageManager.getItem('disableAudioPlayer') === 'true';
  });

  const [recitationId, setRecitationId] = useState(() => {
    return storageManager.getItem('recitationId') || 7;
  });

  const [currentlyPlayingAyah, setCurrentlyPlayingAyah] = useState(null);
  const [currentlyPlayingSurah, setCurrentlyPlayingSurah] = useState(null);
  const [activeWordLocation, setActiveWordLocation] = useState(null);
  const [chapterSnapshot, setChapterSnapshot] = useState(null);

  const [globalLoopCount, setGlobalLoopCount] = useState(() => {
    const stored = storageManager.getItem('ayahLoopCount');
    return stored ? (stored === 'Infinity' ? Infinity : parseInt(stored, 10)) : 1;
  });

  const [playedAyahs, setPlayedAyahs] = useState(() => {
    try {
      return storageManager.getItem('playedAyahs') || {};
    } catch (e) {
      return {};
    }
  });
  const [totalPlaybackTime, setTotalPlaybackTime] = useState(() => {
    return parseInt(storageManager.getItem('totalPlaybackTime'), 10) || 0;
  });

  const recitationIdRef = useRef(recitationId);
  const globalLoopCountRef = useRef(globalLoopCount);
  useEffect(() => {
    recitationIdRef.current = recitationId;
  }, [recitationId]);
  useEffect(() => {
    globalLoopCountRef.current = globalLoopCount;
  }, [globalLoopCount]);

  const trackAyahPlayed = (ayahKey, playbackDuration) => {
    setPlayedAyahs((prev) => {
      const newPlayedAyahs = { ...prev };
      if (!newPlayedAyahs[ayahKey]) {
        newPlayedAyahs[ayahKey] = {
          count: 1,
          lastPlayed: new Date().toISOString(),
        };
      } else {
        newPlayedAyahs[ayahKey] = {
          count: newPlayedAyahs[ayahKey].count + 1,
          lastPlayed: new Date().toISOString(),
        };
      }
      return newPlayedAyahs;
    });
    setTotalPlaybackTime((prev) => prev + (playbackDuration || 0));
  };

  const trackAyahPlayedRef = useRef(trackAyahPlayed);
  trackAyahPlayedRef.current = trackAyahPlayed;

  const chapterAudio = useMemo(
    () =>
      createChapterAudioController({
        setCurrentlyPlayingAyah,
        setActiveWordLocation,
        trackAyahPlayed: (...args) => trackAyahPlayedRef.current(...args),
        getLoopCount: () => globalLoopCountRef.current,
        getRecitationId: () => recitationIdRef.current,
      }),
    []
  );

  useEffect(() => {
    return chapterAudio.subscribe((snap) => {
      setChapterSnapshot({ ...snap });
      // Keep legacy flag: chapter mode marks "surah playing"
      if (snap.mode === 'chapter' && !snap.isPaused) {
        setCurrentlyPlayingSurah(snap.audio);
      } else if (snap.mode !== 'chapter' || snap.isPaused) {
        setCurrentlyPlayingSurah((prev) => (prev === snap.audio ? null : prev));
      }
    });
  }, [chapterAudio]);

  useEffect(() => {
    storageManager.setItem(
      'ayahLoopCount',
      globalLoopCount === Infinity ? 'Infinity' : globalLoopCount
    );
  }, [globalLoopCount]);

  useEffect(() => {
    storageManager.setItem('playedAyahs', playedAyahs);
  }, [playedAyahs]);

  useEffect(() => {
    storageManager.setItem('totalPlaybackTime', totalPlaybackTime.toString());
  }, [totalPlaybackTime]);

  useEffect(() => {
    return () => {
      chapterAudio.stop();
    };
  }, [chapterAudio]);

  const toggleAudioPlayer = (isDisabled) => {
    setIsAudioPlayerDisabled(isDisabled);
    storageManager.setItem('disableAudioPlayer', isDisabled);
  };

  const changeRecitationId = (id) => {
    setRecitationId(id);
    storageManager.setItem('recitationId', id);
    setActiveWordLocation(null);
    chapterAudio.onRecitationChange();
  };

  return (
    <AudioContext.Provider
      value={{
        isAudioPlayerDisabled,
        setIsAudioPlayerDisabled: toggleAudioPlayer,
        toggleAudioPlayer,
        recitationId,
        changeRecitationId,
        currentlyPlayingAyah,
        setCurrentlyPlayingAyah,
        currentlyPlayingSurah,
        setCurrentlyPlayingSurah,
        activeWordLocation,
        setActiveWordLocation,
        globalLoopCount,
        setGlobalLoopCount,
        playedAyahs,
        totalPlaybackTime,
        trackAyahPlayed,
        // Shared chapter audio (ayah + full surah use the same stream)
        chapterAudio,
        chapterSnapshot,
        // Legacy no-ops kept so older imports don't crash
        preloadedAudios: {},
        addPreloadedAudio: () => {},
        removePreloadedAudio: () => {},
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};
